// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState } from 'react';
import { SelectionTree, useUniqueId } from '@cloudscape-design/component-toolkit/internal';
import { fireNonCancelableEvent } from '../../internal/events';
import { joinStrings } from '../../internal/utils/strings';
import { getTrackableValue } from '../utils';
export function useGroupSelection({ ariaLabels, items, rootItems, selectedItems = [], totalItemsCount, trackBy, expandableRows, selectionType, getLoadingStatus, setLastUserAction, }) {
    // The name assigned to all controls to combine them in a single group.
    const selectionControlName = useUniqueId();
    const [shiftPressed, setShiftPressed] = useState(false);
    const [lastClickedItem, setLastClickedItem] = useState(null);
    if (selectionType !== 'group') {
        return { isItemSelected: () => false };
    }
    const getParent = (item) => expandableRows.getExpandableItemProps(item).parent;
    const getChildren = (item) => expandableRows.getExpandableItemProps(item).children;
    const getSelectionStateProps = (item) => {
        const { itemsCount, selectedItemsCount } = expandableRows.getExpandableItemProps(item);
        return { itemsCount, selectedItemsCount };
    };
    const isComplete = (item) => !getLoadingStatus || getLoadingStatus(item) === 'finished';
    const treeProps = { rootItems, trackBy, getChildren, isComplete };
    const selectionTree = new SelectionTree(rootItems, treeProps, expandableRows.groupSelection);
    const onChange = (groupSelection) => {
        fireNonCancelableEvent(expandableRows.onGroupSelectionChange, { groupSelection });
        setLastUserAction === null || setLastUserAction === void 0 ? void 0 : setLastUserAction('selection');
    };
    // Shift-selection helpers.
    const itemIndexesMap = new Map();
    items.forEach((item, i) => itemIndexesMap.set(getTrackableValue(trackBy, item), i));
    const getShiftSelectedItems = (item) => {
        const lastClickedItemIndex = lastClickedItem
            ? itemIndexesMap.get(getTrackableValue(trackBy, lastClickedItem))
            : undefined;
        // We use lastClickedItemIndex to determine if filtering/sorting/pagination
        // made previously selected item invisible, therefore we reset state for shift-select.
        if (lastClickedItemIndex !== undefined) {
            const currentItemIndex = itemIndexesMap.get(getTrackableValue(trackBy, item));
            const start = Math.min(currentItemIndex, lastClickedItemIndex);
            const end = Math.max(currentItemIndex, lastClickedItemIndex);
            const requestedItems = items.slice(start, end + 1);
            return lastClickedItemIndex < currentItemIndex ? requestedItems : requestedItems.reverse();
        }
        return [item];
    };
    const handleToggleAll = () => onChange(selectionTree.toggleAll().getState());
    const handleToggleItem = (item) => {
        setLastClickedItem(item);
        const requestedItems = shiftPressed ? getShiftSelectedItems(item) : [item];
        const requestedItemParents = requestedItems.reduce((set, item) => set.add(getParent(item)), new Set());
        // Shift-selection is only allowed on items with the same parent.
        if (requestedItemParents.size === 1) {
            onChange(selectionTree.toggleSome(requestedItems).getState());
        }
    };
    const createLoaderToggleHandle = (item) => () => onChange(!item ? selectionTree.invertAll().getState() : selectionTree.invertOne(item).getState());
    return {
        isItemSelected: selectionTree.isItemSelected,
        getSelectAllProps: () => {
            var _a, _b, _c;
            return ({
                name: selectionControlName,
                selectionType: 'multi',
                disabled: false,
                checked: selectionTree.isAllItemsSelected(),
                indeterminate: selectionTree.isAllItemsIndeterminate(),
                onChange: handleToggleAll,
                ariaLabel: joinStrings(ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.selectionGroupLabel, (_a = ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.allItemsSelectionLabel) === null || _a === void 0 ? void 0 : _a.call(ariaLabels, {
                    selectedItems,
                    itemsCount: (_b = expandableRows.totalItemsCount) !== null && _b !== void 0 ? _b : totalItemsCount,
                    selectedItemsCount: (_c = expandableRows.totalSelectedItemsCount) !== null && _c !== void 0 ? _c : selectedItems.length,
                })),
            });
        },
        getItemSelectionProps: item => {
            var _a;
            return ({
                name: selectionControlName,
                selectionType: 'multi',
                disabled: false,
                checked: selectionTree.isItemSelected(item),
                indeterminate: selectionTree.isItemIndeterminate(item),
                onChange: () => handleToggleItem(item),
                onShiftToggle: value => setShiftPressed(value),
                ariaLabel: joinStrings(ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.selectionGroupLabel, (_a = ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.itemSelectionLabel) === null || _a === void 0 ? void 0 : _a.call(ariaLabels, { selectedItems, ...getSelectionStateProps(item) }, item)),
            });
        },
        getLoaderSelectionProps: item => {
            var _a;
            return ({
                name: selectionControlName,
                selectionType: 'multi',
                disabled: false,
                checked: item ? selectionTree.isItemSelected(item) : selectionTree.getState().inverted,
                indeterminate: false,
                onChange: createLoaderToggleHandle(item),
                ariaLabel: joinStrings(ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.selectionGroupLabel, (_a = ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.itemLoaderSelectionLabel) === null || _a === void 0 ? void 0 : _a.call(ariaLabels, { selectedItems }, item)),
            });
        },
    };
}
//# sourceMappingURL=use-group-selection.js.map